/**
 * Navigation Mode Store
 *
 * Manages the navigation mode preference (spatial vs classic).
 * Persists to localStorage for user preference retention.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type NavigationMode = 'spatial' | 'classic';

interface NavigationModeStore {
  /** Current navigation mode */
  mode: NavigationMode;

  /** Set the navigation mode */
  setMode: (mode: NavigationMode) => void;

  /** Toggle between spatial and classic modes */
  toggleMode: () => void;

  /** Check if spatial mode is active */
  isSpatialMode: () => boolean;

  /** Check if classic mode is active */
  isClassicMode: () => boolean;
}

export const useNavigationModeStore = create<NavigationModeStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Default to classic (tabs) mode
        mode: 'classic',

        setMode: (mode) =>
          set({ mode }, false, `setMode:${mode}`),

        toggleMode: () =>
          set(
            (state) => ({
              mode: state.mode === 'spatial' ? 'classic' : 'spatial',
            }),
            false,
            'toggleMode'
          ),

        isSpatialMode: () => get().mode === 'spatial',

        isClassicMode: () => get().mode === 'classic',
      }),
      {
        name: 'navigation-mode-storage',
        partialize: (state) => ({ mode: state.mode }),
      }
    ),
    {
      name: 'navigation-mode-store',
    }
  )
);

// Selectors
export const selectNavigationMode = (state: NavigationModeStore) => state.mode;
export const selectIsSpatialMode = (state: NavigationModeStore) =>
  state.mode === 'spatial';
export const selectIsClassicMode = (state: NavigationModeStore) =>
  state.mode === 'classic';
